import { useEffect, useState } from 'react';
import { useApi } from '../../services/api';
import { formatDate } from '../../utils/dateFormatter';

interface Tip {
    id: string;
    title: string;
    content: string;
    category: string;
    isActive: boolean;
    createdAt: string;
}

export default function TipsManagement() {
    const api = useApi();
    const [tips, setTips] = useState<Tip[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<{ category?: string; isActive?: boolean }>({});
    const [editingTip, setEditingTip] = useState<Tip | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        category: 'Best Practices',
        isActive: true,
    });

    const categories = [
        'Best Practices',
        'Productivity',
        'Code Quality',
        'Testing',
        'Collaboration',
        'Tools',
        'Security',
        'Performance',
    ];

    useEffect(() => {
        loadTips();
    }, [filter]);

    async function loadTips() {
        try {
            setLoading(true);
            const result = await api.getTips(filter);
            setTips(result.tips);
        } catch (error) {
            console.error('Failed to load tips:', error);
        } finally {
            setLoading(false);
        }
    }

    async function handleCreate() {
        try {
            await api.createTip(formData);
            setFormData({ title: '', content: '', category: 'Best Practices', isActive: true });
            setIsCreating(false);
            loadTips();
        } catch (error) {
            console.error('Failed to create tip:', error);
            alert('Failed to create tip');
        }
    }

    async function handleUpdate() {
        if (!editingTip) return;
        try {
            await api.updateTip(editingTip.id, formData);
            setEditingTip(null);
            setFormData({ title: '', content: '', category: 'Best Practices', isActive: true });
            loadTips();
        } catch (error) {
            console.error('Failed to update tip:', error);
            alert('Failed to update tip');
        }
    }

    async function handleDelete(id: string) {
        if (!confirm('Are you sure you want to delete this tip?')) return;
        try {
            await api.deleteTip(id);
            loadTips();
        } catch (error) {
            console.error('Failed to delete tip:', error);
            alert('Failed to delete tip');
        }
    }

    async function toggleActive(tip: Tip) {
        try {
            await api.updateTip(tip.id, { isActive: !tip.isActive });
            loadTips();
        } catch (error) {
            console.error('Failed to toggle tip:', error);
            alert('Failed to toggle tip');
        }
    }

    function startEdit(tip: Tip) {
        setEditingTip(tip);
        setFormData({
            title: tip.title,
            content: tip.content,
            category: tip.category,
            isActive: tip.isActive,
        });
        setIsCreating(false);
    }

    function cancelEdit() {
        setEditingTip(null);
        setIsCreating(false);
        setFormData({ title: '', content: '', category: 'Best Practices', isActive: true });
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-text-primary">Tips Management</h1>
                <button
                    onClick={() => {
                        setIsCreating(true);
                        setEditingTip(null);
                        setFormData({ title: '', content: '', category: 'Best Practices', isActive: true });
                    }}
                    className="bg-primary text-white px-4 py-2 rounded-lg hover:opacity-90 transition"
                >
                    ➕ Create Tip
                </button>
            </div>

            {/* Filters */}
            <div className="bg-surface rounded-lg shadow p-4 mb-6 border border-border">
                <div className="flex flex-wrap gap-4">
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Category</label>
                        <select
                            value={filter.category || ''}
                            onChange={(e) => setFilter({ ...filter, category: e.target.value || undefined })}
                            className="border border-border rounded-lg px-3 py-2 bg-surface text-text-primary"
                        >
                            <option value="">All Categories</option>
                            {categories.map((cat) => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Status</label>
                        <select
                            value={filter.isActive === undefined ? '' : filter.isActive.toString()}
                            onChange={(e) =>
                                setFilter({
                                    ...filter,
                                    isActive: e.target.value === '' ? undefined : e.target.value === 'true',
                                })
                            }
                            className="border border-border rounded-lg px-3 py-2 bg-surface text-text-primary"
                        >
                            <option value="">All</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Create/Edit Form */}
            {(isCreating || editingTip) && (
                <div className="bg-surface rounded-lg shadow p-6 mb-6 border border-border">
                    <h2 className="text-xl font-bold mb-4 text-text-primary">{editingTip ? 'Edit Tip' : 'Create New Tip'}</h2>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">Title</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                className="w-full border border-border rounded-lg px-3 py-2 bg-surface text-text-primary"
                                placeholder="e.g., Code Reviews Matter"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">Content</label>
                            <textarea
                                value={formData.content}
                                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                className="w-full border border-border rounded-lg px-3 py-2 h-24 bg-surface text-text-primary"
                                placeholder="Enter the tip content..."
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Category</label>
                                <select
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    className="w-full border border-border rounded-lg px-3 py-2 bg-surface text-text-primary"
                                >
                                    {categories.map((cat) => (
                                        <option key={cat} value={cat}>
                                            {cat}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Status</label>
                                <select
                                    value={formData.isActive.toString()}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'true' })}
                                    className="w-full border border-border rounded-lg px-3 py-2 bg-surface text-text-primary"
                                >
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={editingTip ? handleUpdate : handleCreate}
                                className="bg-primary text-white px-4 py-2 rounded-lg hover:opacity-90 transition"
                            >
                                {editingTip ? 'Update' : 'Create'}
                            </button>
                            <button
                                onClick={cancelEdit}
                                className="bg-background text-text-secondary px-4 py-2 rounded-lg hover:bg-border border border-border transition"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tips List */}
            <div className="bg-surface rounded-lg shadow border border-border">
                <div className="p-4 border-b border-border">
                    <h2 className="text-lg font-semibold text-text-primary">
                        All Tips ({tips.length})
                    </h2>
                </div>
                {loading ? (
                    <div className="p-12 text-center text-text-muted">Loading...</div>
                ) : tips.length === 0 ? (
                    <div className="p-12 text-center text-text-muted">No tips found</div>
                ) : (
                    <div className="divide-y divide-border">
                        {tips.map((tip) => (
                            <div key={tip.id} className="p-4 hover:bg-background transition">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <h3 className="font-semibold text-lg text-text-primary">{tip.title}</h3>
                                            <span
                                                className={`px-2 py-1 rounded-full text-xs font-semibold ${tip.isActive
                                                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                                    : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                                                    }`}
                                            >
                                                {tip.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        <p className="text-text-secondary mb-2">{tip.content}</p>
                                        <div className="flex items-center space-x-2 text-sm text-text-muted">
                                            <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full text-xs font-semibold border border-blue-500/30">
                                                {tip.category}
                                            </span>
                                            <span>•</span>
                                            <span>Created {formatDate(tip.createdAt)}</span>
                                        </div>
                                    </div>
                                    <div className="flex space-x-2 ml-4">
                                        <button
                                            onClick={() => toggleActive(tip)}
                                            className="text-text-secondary hover:text-text-primary p-2"
                                            title={tip.isActive ? 'Deactivate' : 'Activate'}
                                        >
                                            {tip.isActive ? '👁️' : '🚫'}
                                        </button>
                                        <button
                                            onClick={() => startEdit(tip)}
                                            className="text-primary hover:opacity-80 p-2"
                                            title="Edit"
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            onClick={() => handleDelete(tip.id)}
                                            className="text-red-400 hover:text-red-300 p-2"
                                            title="Delete"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
